import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WordPressPluginCard from '../../components/integration/WordPressPluginCard';
import { toast } from 'react-hot-toast';
import { setupAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './AssetSetupPage.css';
import AssetPlatformFields, { emptyAssetPlatformValues, hasAnyPlatform } from '../../components/assets/AssetPlatformFields';

// ─── Welcome Feature Slideshow ─────────────────────────────────────────────

const WELCOME_SLIDES = [
  {
    icon: 'bi-bar-chart-line-fill',
    color: '#6366f1',
    title: 'Real-Time Analytics',
    desc: 'Track visitors, page views, events, and conversions across all your digital assets — live.',
  },
  {
    icon: 'bi-activity',
    color: '#0ea5e9',
    title: 'Uptime Monitoring',
    desc: 'Get instant alerts when your website goes down. Multi-region checks every 5 minutes.',
  },
  {
    icon: 'bi-shield-check',
    color: '#10b981',
    title: 'Security Scanning',
    desc: 'AI-powered vulnerability detection and blacklist monitoring for your domains and IP addresses.',
  },
  {
    icon: 'bi-server',
    color: '#f59e0b',
    title: 'Server Monitoring',
    desc: 'Monitor CPU, memory, disk, and running services on your servers. Get alerted on critical issues.',
  },
  {
    icon: 'bi-envelope-check-fill',
    color: '#ec4899',
    title: 'Email Security',
    desc: 'Validate SPF, DKIM, DMARC records and monitor if your domain lands on email blacklists.',
  },
];

const WelcomeModal: React.FC<{ name: string; onClose: () => void }> = ({ name, onClose }) => {
  const [slide, setSlide] = useState(0);
  const total = WELCOME_SLIDES.length;
  const s = WELCOME_SLIDES[slide];

  useEffect(() => {
    const t = setTimeout(() => {
      if (slide < total - 1) setSlide(slide + 1);
    }, 3500);
    return () => clearTimeout(t);
  }, [slide]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 20, maxWidth: 480, width: '100%',
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        animation: 'fadeInScale .3s ease',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          padding: '28px 32px 20px', color: '#fff',
        }}>
          <div className="d-flex align-items-center gap-3 mb-2">
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-stars" style={{ fontSize: 22 }}></i>
            </div>
            <div>
              <h4 className="mb-0 fw-bold">Welcome, {name}! 🎉</h4>
              <p className="mb-0 small" style={{ opacity: .85 }}>Your subscription is active</p>
            </div>
          </div>
        </div>

        {/* Slide */}
        <div style={{ padding: '28px 32px' }}>
          <div style={{
            textAlign: 'center', minHeight: 160,
            transition: 'opacity .3s', opacity: 1,
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: s.color + '18',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <i className={`bi ${s.icon}`} style={{ fontSize: 32, color: s.color }}></i>
            </div>
            <h5 className="fw-bold mb-2">{s.title}</h5>
            <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>{s.desc}</p>
          </div>

          {/* Dots */}
          <div className="d-flex justify-content-center gap-2 mt-4 mb-4">
            {WELCOME_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                style={{
                  width: i === slide ? 24 : 8, height: 8, borderRadius: 4,
                  border: 'none', padding: 0, cursor: 'pointer',
                  background: i === slide ? s.color : '#d1d5db',
                  transition: 'all .3s',
                }}
              />
            ))}
          </div>

          <div className="d-flex gap-3">
            {slide < total - 1 ? (
              <>
                <button className="btn btn-outline-secondary flex-fill" onClick={onClose}>
                  Skip
                </button>
                <button
                  className="btn flex-fill text-white"
                  style={{ background: s.color }}
                  onClick={() => setSlide(slide + 1)}
                >
                  Next <i className="bi bi-arrow-right ms-1"></i>
                </button>
              </>
            ) : (
              <button
                className="btn flex-fill text-white fw-semibold"
                style={{ background: '#6366f1' }}
                onClick={onClose}
              >
                <i className="bi bi-rocket-takeoff me-2"></i>Get Started
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(.92); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

const ModernAssetSetupPage: React.FC = () => {
  useDocumentTitle('Setup Asset');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(searchParams.get('welcome') === '1');

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    // Remove ?welcome=1 from URL without reload
    setSearchParams({}, { replace: true });
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ...emptyAssetPlatformValues(),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [createdAsset, setCreatedAsset] = useState<any>(null);
  const [integrationTab, setIntegrationTab] = useState<'script' | 'wordpress'>('script');
  const navigate = useNavigate();
  const { user, setAssets, setCurrentAsset, logout } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const getTrackingCode = () => {
    if (!createdAsset) return '';

    // Use the integration_code from the API response (already has correct org ID and tracking ID)
    if (createdAsset.integration_code) {
      return `<!-- CloudAyn Analytics Tracking -->\n${createdAsset.integration_code}`;
    }

    // Fallback: build manually using organisation_id + tracking_id
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const trackingId = createdAsset.tracking_id || '';
    const orgId = createdAsset.organisation_id;

    return `<!-- CloudAyn Analytics Tracking -->
<script src="${apiUrl}/v1/integration/${orgId}/${trackingId}/cloudayn.js" async></script>`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter an asset name');
      return;
    }

    if (!hasAnyPlatform(formData)) {
      toast.error('Please select at least one platform');
      return;
    }

    if (formData.platform_website && formData.website_urls.some(url => !url.trim())) {
      toast.error('Please provide valid website URLs');
      return;
    }

    setIsLoading(true);
    try {
      const response = await setupAPI.createAsset(formData);
      if (response.data.status === 'success') {
        if (response.data.assets) {
          setAssets(response.data.assets);
        }
        if (response.data.asset) {
          setCurrentAsset(response.data.asset);
          setCreatedAsset(response.data.asset);
        }

        toast.success('Asset created successfully!');
        setShowCompletion(true);
      } else {
        toast.error(response.data.message || 'Failed to create asset');
      }
    } catch (error: any) {
      console.error('Asset creation error:', error);
      toast.error(error.response?.data?.message || 'Failed to create asset. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="asset-setup-page">
      {showWelcome && (
        <WelcomeModal name={user?.name || 'there'} onClose={handleCloseWelcome} />
      )}
      {/* Top Header */}
      <div className="asset-setup-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div className="logo-icon me-2">
                <span className="logo-text">C</span>
              </div>
              <span className="brand-name">CloudAyn</span>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="user-info d-none d-md-flex align-items-center">
                <div className="user-avatar">
                  <i className="bi bi-person-circle"></i>
                </div>
                <div className="ms-2">
                  <div className="user-name">{user?.name}</div>
                  <div className="user-email">{user?.email}</div>
                </div>
              </div>
              <button className="btn btn-outline-danger btn-sm" onClick={logout}>
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="asset-setup-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              {/* Progress Header */}
              <div className="setup-progress-header mb-5 text-center">
                <div className="progress-indicator mb-4">
                  <div className="progress-step completed">
                    <div className="step-circle">
                      <i className="bi bi-check"></i>
                    </div>
                    <span className="step-label">Account</span>
                  </div>
                  <div className={`progress-line ${showCompletion ? 'completed' : 'active'}`}></div>
                  <div className={`progress-step ${showCompletion ? 'completed' : 'active'}`}>
                    <div className="step-circle">
                      {showCompletion ? <i className="bi bi-check"></i> : '2'}
                    </div>
                    <span className="step-label">Asset</span>
                  </div>
                  <div className={`progress-line ${showCompletion ? 'active' : ''}`}></div>
                  <div className={`progress-step ${showCompletion ? 'active' : ''}`}>
                    <div className="step-circle">3</div>
                    <span className="step-label">Integration</span>
                  </div>
                </div>
                <h1 className="setup-title">
                  {showCompletion ? 'Setup Complete! 🎉' : 'Add Your First Digital Asset'}
                </h1>
                <p className="setup-subtitle">
                  {showCompletion
                    ? 'Add the tracking code to your website to start monitoring'
                    : 'Tell us about the website, app, or platform you want to monitor'}
                </p>
              </div>

              {/* Completion Screen */}
              {showCompletion && createdAsset ? (
                <div className="setup-form-card">
                  <div className="completion-content text-center mb-4">
                    <div className="completion-icon mb-4">
                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h2 className="mb-3">Asset Created Successfully!</h2>
                    <p className="text-muted mb-4">
                      Your asset "<strong>{createdAsset.name}</strong>" has been created.
                      Choose how you'd like to integrate CloudAyn into your website.
                    </p>
                  </div>

                  {/* Integration method tabs */}
                  <div className="form-section">
                    <h3 className="section-title">
                      <i className="bi bi-plug me-2"></i>
                      Integration Method
                    </h3>

                    {/* Tab buttons */}
                    <div className="d-flex gap-2 mb-4 flex-wrap">
                      {[
                        { key: 'script', icon: 'bi-code-square', label: 'Tracking Script' },
                        { key: 'wordpress', icon: 'bi-wordpress', label: 'WordPress Plugin' },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          className={`btn ${integrationTab === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setIntegrationTab(tab.key as 'script' | 'wordpress')}
                          style={{ borderRadius: 8 }}
                        >
                          <i className={`bi ${tab.icon} me-2`}></i>
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Script tab */}
                    {integrationTab === 'script' && formData.platform_website && (
                      <>
                        <p className="mb-3">
                          Add this code to the <code>&lt;head&gt;</code> section of your website, just before the closing <code>&lt;/head&gt;</code> tag:
                        </p>
                        <div className="code-block-container position-relative">
                          <button
                            className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-2"
                            onClick={() => copyToClipboard(getTrackingCode())}
                            style={{ zIndex: 10 }}
                          >
                            <i className="bi bi-clipboard me-1"></i>
                            Copy Code
                          </button>
                          <pre className="code-block p-3 bg-dark text-light rounded" style={{ maxHeight: '300px', overflow: 'auto' }}>
                            <code>{getTrackingCode()}</code>
                          </pre>
                        </div>
                      </>
                    )}

                    {integrationTab === 'script' && !formData.platform_website && (
                      <div className="alert alert-secondary">
                        <i className="bi bi-info-circle me-2"></i>
                        Tracking script is available for website assets. Visit the Integration page for more options.
                      </div>
                    )}

                    {/* WordPress tab */}
                    {integrationTab === 'wordpress' && (
                      <WordPressPluginCard />
                    )}
                  </div>

                  <div className="alert alert-info mt-4" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Next Steps:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Add the tracking script or install the WordPress plugin</li>
                      <li>Data will start appearing in your dashboard within minutes</li>
                      <li>You can find all integration options later in the Integration page</li>
                    </ul>
                  </div>

                  <div className="form-actions mt-4">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                      <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                    {createdAsset?.id && (
                      <button
                        className="btn btn-outline-primary btn-lg ms-2"
                        onClick={() => navigate(`/onboarding/${createdAsset.id}`)}
                      >
                        <i className="bi bi-list-check me-2"></i>
                        Guided setup checklist
                      </button>
                    )}
                    <button
                      className="btn btn-outline-secondary btn-lg ms-2"
                      onClick={() => navigate('/integration')}
                    >
                      <i className="bi bi-code-slash me-2"></i>
                      View Integration Page
                    </button>
                  </div>
                </div>
              ) : (
                /* Setup Form Card */
                <div className="setup-form-card">
                  <form onSubmit={handleSubmit}>
                  {/* Basic Information */}
                  <div className="form-section">
                    <h3 className="section-title">
                      <i className="bi bi-info-circle me-2"></i>
                      Basic Information
                    </h3>

                    <div className="row">
                      <div className="col-12 mb-3">
                        <label className="form-label">Asset Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="My Website/App"
                          required
                          disabled={isLoading}
                        />
                      </div>

                      <div className="col-12 mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          name="description"
                          rows={3}
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="Brief description of your asset"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Asset types + per-type settings (shared with Edit asset) */}
                  <AssetPlatformFields
                    value={formData}
                    onChange={(next) => setFormData(prev => ({ ...prev, ...next }))}
                    disabled={isLoading}
                  />

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creating Asset...
                        </>
                      ) : (
                        <>
                          Create Asset & Continue
                          <i className="bi bi-arrow-right ms-2"></i>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernAssetSetupPage;
